import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui-components/ui/tabs';
import { Button } from '../../../ui-components/ui/button';
import { Badge } from '../../../ui-components/ui/badge';
import { 
  Activity, 
  FileText, 
  Mail, 
  Files, 
  MessageSquare,
  Users,
  Building2,
  Phone,
  MapPin,
  Check,
  X,
  Calendar,
  Clock,
  ChevronDown,
  User,
  MoreVertical,
  Trash2,
  Archive,
  RefreshCcw
} from 'lucide-react';
import useApiRequest from '../../../lib/hooks/use-request';
import { DealDetailsResponseDto } from '../../../lib/interfaces/deals';
import TitleForm from '../../../ui-components/ui/title-form';
import DataFetchParent from '../../../ui-components/ui/data-fetch';
import PageNotFound from '../../error/page-not-found';
import { PageTitle, PageTitleContainer } from '../../../ui-components/utils/menu-items/page-title';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui-components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../../ui-components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui-components/ui/card';
import { Avatar, AvatarFallback } from '../../../ui-components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui-components/ui/dialog';
import { toast } from 'sonner';
import { useUser } from '../../../lib/hooks/use-user';
import { UserRole } from '../../../lib/interfaces/user';

// Import our new components
import { DealHeaderSimple } from '../components/deal-header-simple';
import { ActivityTimeline } from '../components/activity-timeline';
import { QuickActivityButtons } from '../components/quick-activity-buttons';
import { AppointmentModal } from '../../calendar/components/appointment-modal';
import { useAppointments } from '../../../lib/hooks/useAppointments';
import DealCalendar from '../components/deal-calendar';
import { DealCalendarSidebar } from '../components/deal-calendar-sidebar';
import '../components/tabs-scrollbar.css';
import { 
  Appointment, 
  CreateAppointmentDto, 
  UpdateAppointmentDto, 
  AppointmentType 
} from '../../../lib/interfaces/appointment.interface';
import DealHistory from '../history';
import InformationForm from '../information-form';
import { DealOutcomeModal, OutcomeData } from '../components/deal-outcome-modal';
import { FileUpload } from '../components/file-upload';
import EmailTemplates from '../components/email-templates';

const DealPageEnhanced = () => {
  const { id } = useParams();
  return <>{id ? <DealPageEnhancedContent id={id} /> : <PageNotFound />}</>;
};

const DealPageEnhancedContent = ({ id }: { id: string }) => {
  const [deal, setDeal] = useState<DealDetailsResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentType, setAppointmentType] = useState<AppointmentType>(AppointmentType.MEETING);
  const [reload, setReload] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState<string>('');
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [filesCounts, setFilesCounts] = useState({ files: 0, documents: 0 });
  const [ownerOverride, setOwnerOverride] = useState<{ id: string; name: string } | null>(null);
  
  const { apiRequest } = useApiRequest();
  const { createAppointment, updateAppointment } = useAppointments();
  const { user } = useUser();
  const location = useLocation() as any;

  // Helper to get owner name from selectableEntities
  const derivedOwnerName = useMemo(() => {
    if (!deal) return '';
    if (ownerOverride) return ownerOverride.name;
    if (deal.selectableEntities?.users && deal.belongsToUserId) {
      const owner = deal.selectableEntities.users.find(u => u._id === deal.belongsToUserId);
      if (owner?.value) return owner.value;
    }
    return deal.belongsToUserFullName || '';
  }, [deal, ownerOverride]);

  // Helper to compute createdAt from Mongo ObjectId if missing
  const getCreatedAtDate = (deal: DealDetailsResponseDto): Date | undefined => {
    if (deal.createdAt) {
      try {
        return new Date(deal.createdAt);
      } catch (_) {
        // ignore
      }
    }
    try {
      if (deal._id && deal._id.length >= 8) {
        const ts = parseInt(deal._id.substring(0, 8), 16) * 1000;
        return new Date(ts);
      }
    } catch (_) {
      // ignore
    }
    return undefined;
  };

  const fetchDeal = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<DealDetailsResponseDto>(`deals/${id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      let enriched = response.data;

      // If owner full name is missing, try to resolve from selectableEntities
      if (!enriched.belongsToUserFullName && enriched.selectableEntities?.users && enriched.belongsToUserId) {
        const owner = enriched.selectableEntities.users.find(u => u._id === enriched.belongsToUserId);
        if (owner?.value) enriched = { ...enriched, belongsToUserFullName: owner.value } as any;
      }

      // If pipeline/stage titles are missing, fetch pipeline details and resolve
      if ((!enriched.pipelineTitle || !enriched.stageTitle) && enriched.belongsToPipelineId) {
        try {
          const pipelineRes = await apiRequest<any>(`pipelines/${enriched.belongsToPipelineId}`, "GET");
          const p = pipelineRes.data;
          if (p) {
            let stageTitle = enriched.stageTitle;
            if (!stageTitle && (enriched as any).belongsToStageId && Array.isArray(p.stages)) {
              const stage = p.stages.find((s: any) => s._id === (enriched as any).belongsToStageId);
              if (stage?.title) stageTitle = stage.title;
            }
            enriched = {
              ...enriched,
              pipelineTitle: enriched.pipelineTitle || p.title,
              stageTitle: stageTitle || enriched.stageTitle,
            } as any;
          }
        } catch (_) {
          // ignore – best-effort enrichment
        }
      }

      // If owner name still missing, try global creation entities users list
      if (!enriched.belongsToUserFullName && enriched.belongsToUserId) {
        try {
          const entitiesRes = await apiRequest<any>('deals/creation-entities', 'GET');
          const users: any[] = entitiesRes?.data?.users || entitiesRes?.data?.selectableEntities?.users || [];
          const match = users.find((u: any) => u._id === enriched.belongsToUserId);
          if (match) {
            const fullName = match.value || (match.firstName && match.lastName ? `${match.firstName} ${match.lastName}` : undefined);
            if (fullName) {
              enriched = { ...enriched, belongsToUserFullName: fullName } as any;
            }
          }
        } catch (_) {
          // ignore – best-effort enrichment
        }
      }

      // Final fallback: read owner from pipeline deals list (kanban source)
      if (!enriched.belongsToUserFullName && enriched.belongsToPipelineId) {
        try {
          const dealsInPipeline = await apiRequest<any>(`deals/pipelines/${enriched.belongsToPipelineId}`, 'GET');
          const allDeals = dealsInPipeline?.data?.deals || dealsInPipeline?.data || [];
          const self = (Array.isArray(allDeals) ? allDeals : []).find((d: any) => d?._id === enriched._id);
          if (self?.belongsToUserFullName) {
            enriched = { ...enriched, belongsToUserFullName: self.belongsToUserFullName } as any;
          }
        } catch (_) {
          // ignore – best-effort enrichment
        }
      }

      // If createdAt missing, derive from ObjectId as fallback
      if (!enriched.createdAt && enriched._id && (enriched._id as any).substring) {
        try {
          const ts = parseInt((enriched._id as unknown as string).substring(0, 8), 16) * 1000;
          enriched = { ...enriched, createdAt: new Date(ts).toISOString() } as any;
        } catch (_) {
          // ignore
        }
      }

      // If navigation provided owner name from Kanban, use it
      if (!enriched.belongsToUserFullName && location?.state?.ownerFullName) {
        enriched = { ...enriched, belongsToUserFullName: location.state.ownerFullName } as any;
      }

      // Respect local override until API returns updated full name
      if (ownerOverride && enriched.belongsToUserId === ownerOverride.id) {
        if (!enriched.belongsToUserFullName || enriched.belongsToUserFullName !== ownerOverride.name) {
          enriched = { ...enriched, belongsToUserFullName: ownerOverride.name } as any;
        } else {
          // server now matches our override → clear override
          setOwnerOverride(null);
        }
      }

      setDeal(enriched);
    }
  }, [apiRequest, id, ownerOverride]);

  useEffect(() => {
    fetchDeal();
  }, [fetchDeal, reload]);
  const handleRefresh = async () => {
    setIsLoading(true);
    await fetchDeal();
    setReload(prev => !prev);
    try {
      window.dispatchEvent(new CustomEvent('historyUpdated', { detail: { dealId: id } }));
      window.dispatchEvent(new CustomEvent('appointmentUpdated', { detail: { dealId: id } }));
    } catch (_) {}
    setIsLoading(false);
    toast.success('Daten aktualisiert');
  };


  // Count files for tab badges
  useEffect(() => {
    if (deal?._id) {
      const filesData = localStorage.getItem(`deal_files_${deal._id}`);
      const docsData = localStorage.getItem(`deal_documents_${deal._id}`);
      
      const filesCount = filesData ? JSON.parse(filesData).length : 0;
      const docsCount = docsData ? JSON.parse(docsData).length : 0;
      
      setFilesCounts({ files: filesCount, documents: docsCount });
    }
  }, [deal?._id, reload]);

  const updateDeal = async (field: string, value: any) => {
    // If updating information field, add to history
    if (field === 'information') {
      const historyEntry = {
        _id: `local_${Date.now()}`,
        dealId: id,
        belongsToUserId: user?._id || 'unknown',
        historyType: 'NOTE' as any,
        historyNoteContent: {
          _id: `note_${Date.now()}`,
          description: value || 'Notiz aktualisiert'
        },
        createdAt: new Date().toISOString(),
        belongsToUserFullName: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer',
      };

      const existingHistory = JSON.parse(localStorage.getItem(`deal_history_${id}`) || '[]');
      localStorage.setItem(`deal_history_${id}`, JSON.stringify([historyEntry, ...existingHistory]));
    }

    const response = await apiRequest<DealDetailsResponseDto>(`deals/${id}`, "PATCH", {
      body: { [field]: value }
    });
    if (response.data) {
      setReload(prev => !prev);
      toast.success(field === 'information' ? 'Notiz aktualisiert' : 'Deal aktualisiert');
    }
  };

  const updateTitle = async (title: string) => {
    await updateDeal('title', title);
    setIsEditingTitle(false);
  };


  const handleOwnerChange = async () => {
    if (!newOwnerId || newOwnerId === deal?.belongsToUserId) {
      return;
    }

    // Resolve the new owner name from selectableEntities first, then dummy fallback
    const selectableUser = (deal?.selectableEntities?.users || []).find(u => u._id === newOwnerId);
    const dummyUsers = [
      { _id: '1', value: 'Andreas Osterrieder' },
      { _id: '2', value: 'Andreas Poschmann' },
      { _id: '3', value: 'Christian Kaiser' },
      { _id: '4', value: 'Clemens Karge' },
      { _id: '5', value: 'Dirk Bienert' },
      { _id: '6', value: 'Felix Endres' },
      { _id: '7', value: 'Freddy Blum' },
      { _id: '8', value: 'Helmut Fenzl' },
      { _id: '9', value: 'Jürgen Schlosser' },
      { _id: '10', value: 'Kai Bockelberg' },
      { _id: '11', value: 'Michael Lerf' },
      { _id: '12', value: 'Peter Münstermann' },
      { _id: '13', value: 'Ramon Alvarez' },
      { _id: '14', value: 'Raphaela Sainovic' },
      { _id: '15', value: 'Tobias Bierek' },
      { _id: '16', value: 'Julian Heißwolf' },
      { _id: user?._id || '17', value: user ? `${user.firstName} ${user.lastName}` : 'Andi Hysa' },
      { _id: '18', value: 'Andi Seller Test' },
    ];

    const oldOwnerName = deal?.belongsToUserFullName || 'Unbekannt';
    const dummyMatch = dummyUsers.find(u => u._id === newOwnerId);
    const newOwnerName = selectableUser?.value || dummyMatch?.value || 'Unbekannt';

    // Add history entry for owner change
    const historyEntry = {
      _id: `local_${Date.now()}`,
      dealId: id,
      belongsToUserId: user?._id || 'unknown',
      historyType: 'OWNER_CHANGE' as any,
      historyNoteContent: {
        _id: `note_${Date.now()}`,
        description: `Besitzer\n${oldOwnerName} → ${newOwnerName}`
      },
      createdAt: new Date().toISOString(),
      belongsToUserFullName: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer',
    };

    const existingHistory = JSON.parse(localStorage.getItem(`deal_history_${id}`) || '[]');
    localStorage.setItem(`deal_history_${id}`, JSON.stringify([historyEntry, ...existingHistory]));

    // Update deal in localStorage
    const storedDeal = localStorage.getItem(`deal_${id}`);
    if (storedDeal) {
      const parsedDeal = JSON.parse(storedDeal);
      parsedDeal.belongsToUserId = newOwnerId;
      parsedDeal.belongsToUserFullName = newOwnerName;
      localStorage.setItem(`deal_${id}`, JSON.stringify(parsedDeal));
    }

    // Optimistically update UI
    setOwnerOverride({ id: newOwnerId, name: newOwnerName });
    setDeal(prev => prev ? { ...prev, belongsToUserId: newOwnerId, belongsToUserFullName: newOwnerName } : prev);

    // Try API call but don't fail if it doesn't work
    try {
      await apiRequest<DealDetailsResponseDto>(`deals/${id}/owner`, "PATCH", {
        body: { userId: newOwnerId }
      });
      // Immediately refetch fresh deal from API to reflect server state
      await fetchDeal();
    } catch (error) {
      console.warn('Could not update owner via API:', error);
    }

    toast.success(`Besitzer wurde geändert zu ${newOwnerName}`);
    setNewOwnerId('');
    setReload(prev => !prev);

    // Notify other listeners (e.g., history widgets) if any
    try {
      window.dispatchEvent(new CustomEvent('historyUpdated', { detail: { dealId: id } }));
      window.dispatchEvent(new CustomEvent('dealOwnerChanged', { detail: { dealId: id, newOwnerId, newOwnerName } }));
    } catch (_) {}
  };

  const toggleWin = async (type: 'win' | 'lose') => {
    await apiRequest<DealDetailsResponseDto>(`deals/${id}/${type}`, "PATCH");
    setReload(prev => !prev);
    toast.success(type === 'win' ? 'Deal als gewonnen markiert' : 'Deal als verloren markiert');
  };

  const handleDeleteDeal = async () => {
    try {
      // Try API delete
      await apiRequest(`deals/${id}`, "DELETE");
    } catch (error) {
      console.warn('Could not delete deal via API:', error);
    }

    // Mark as deleted in localStorage
    localStorage.setItem(`deal_deleted_${id}`, 'true');
    
    // Clear related data
    localStorage.removeItem(`deal_${id}`);
    localStorage.removeItem(`appointments_${id}`);
    localStorage.removeItem(`deal_history_${id}`);
    
    toast.success('Deal wurde gelöscht');
    
    // Redirect to deals list
    window.location.href = '/deals';
  };

  const handleArchiveDeal = async () => {
    // Mark as archived in localStorage
    const storedDeal = localStorage.getItem(`deal_${id}`);
    if (storedDeal) {
      const parsedDeal = JSON.parse(storedDeal);
      parsedDeal.archived = true;
      localStorage.setItem(`deal_${id}`, JSON.stringify(parsedDeal));
    }
    
    toast.success('Deal wurde archiviert');
    setReload(prev => !prev);
  };


  const handleActivityCreate = (type: string) => {
    switch (type) {
      case 'email':
        // Navigate to emails tab
        setActiveTab('emails');
        break;
      case 'task':
        // Navigate to tasks page with this deal pre-selected
        window.location.href = `/tasks?dealId=${id}`;
        break;
      case 'note':
        // Scroll to notes section in activity tab
        const notesSection = document.getElementById('notes-section');
        notesSection?.scrollIntoView({ behavior: 'smooth' });
        break;
      default:
        // Handle appointment types (call, meeting)
        let activityType = AppointmentType.MEETING;
        switch (type) {
          case 'call':
            activityType = AppointmentType.CALL;
            break;
          case 'meeting':
            activityType = AppointmentType.MEETING;
            break;
        }
        setAppointmentType(activityType);
        setSelectedAppointment(null);
        setShowAppointmentModal(true);
    }
  };

  const handleActivityClick = (activity: Appointment) => {
    setSelectedAppointment(activity);
    setShowAppointmentModal(true);
  };

  const handleOutcomeSave = async (outcomeData: OutcomeData) => {
    try {
      // Format the outcome for better readability in history
      const outcomeLabels: Record<string, string> = {
        'reached': '✅ Erreicht',
        'not_reached': '📞 Nicht erreicht',
        'meeting_scheduled': '📅 Termin vereinbart',
        'follow_up_needed': '🔄 Follow-up erforderlich',
        'not_interested': '❌ Kein Interesse',
        'other': '📝 Sonstiges'
      };

      const nextActionLabels: Record<string, string> = {
        'call': '📞 Anruf',
        'meeting': '🤝 Meeting',
        'email': '✉️ E-Mail'
      };

      let historyDescription = `${outcomeLabels[outcomeData.outcome] || outcomeData.outcome}`;
      
      if (outcomeData.notes) {
        historyDescription += `\n\n💬 Notizen: ${outcomeData.notes}`;
      }

      if (outcomeData.nextAction) {
        const actionDate = new Date(outcomeData.nextAction.date);
        const formattedDate = actionDate.toLocaleDateString('de-DE', { 
          weekday: 'short', 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        });
        historyDescription += `\n\n📅 Nächste Aktion: ${nextActionLabels[outcomeData.nextAction.type]} am ${formattedDate} um ${outcomeData.nextAction.time} Uhr`;
        historyDescription += `\n📋 ${outcomeData.nextAction.description}`;
      }

      // Save the outcome to deal history
      const historyResponse = await apiRequest(`deals/history/${id}/notes`, "POST", {
        body: {
          description: historyDescription
        }
      });

      if (historyResponse.error) {
        throw new Error('Failed to save outcome');
      }

      // Create next action if provided
      if (outcomeData.nextAction && deal) {
        const startDate = new Date(outcomeData.nextAction.date);
        const [hours, minutes] = outcomeData.nextAction.time.split(':');
        startDate.setHours(parseInt(hours), parseInt(minutes));
        
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        let appointmentType = AppointmentType.TASK;
        switch (outcomeData.nextAction.type) {
          case 'call':
            appointmentType = AppointmentType.CALL;
            break;
          case 'meeting':
            appointmentType = AppointmentType.MEETING;
            break;
          case 'email':
            appointmentType = AppointmentType.EMAIL;
            break;
        }

        const appointmentResult = await createAppointment({
          dealId: deal._id,
          title: `${nextActionLabels[outcomeData.nextAction.type]}: ${outcomeData.nextAction.description}`,
          type: appointmentType,
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          timezone: 'Europe/Berlin',
          allDay: false,
          participantUserIds: deal.belongsToUserId ? [deal.belongsToUserId] : [], // Assign to the deal owner
          contactId: deal.personPreview?._id,
        });

        if (!appointmentResult) {
          toast.error('Fehler beim Erstellen des Kalendereintrags');
        }
      }

      setShowOutcomeModal(false);
      toast.success('Aktivität abgeschlossen' + (outcomeData.nextAction ? ' und Termin erstellt' : ''));
      
      // Refresh the page to show the new history entry and calendar update
      setReload(prev => !prev);
      
      // If on activities tab, refresh the timeline
      if (activeTab === 'activities') {
        // Activities will be refreshed by the setReload
      }
    } catch (error) {
      console.error('Error saving outcome:', error);
      toast.error('Fehler beim Speichern der Aktivität');
    }
  };

  const handleAppointmentSave = async (data: CreateAppointmentDto | UpdateAppointmentDto, appointmentId?: string) => {
    if (appointmentId) {
      await updateAppointment(appointmentId, data as UpdateAppointmentDto);
    } else {
      await createAppointment({
        ...(data as CreateAppointmentDto),
        dealId: id,
        type: appointmentType
      });
    }
    setShowAppointmentModal(false);
    setReload(prev => !prev);
  };

  if (!deal) {
    return (
      <DataFetchParent 
        isLoading={isLoading} 
        data={null}
        renderElement={() => <div>Loading...</div>}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Enhanced Header */}
      <PageTitleContainer>
        <div className="space-y-4">
          {/* Title and Actions Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isEditingTitle ? (
                <TitleForm
                  onSubmit={(values) => updateTitle(values.title)}
                  defaultValue={deal.title}
                  onCancel={() => setIsEditingTitle(false)}
                />
              ) : (
                <PageTitle>
                  <span>{deal.title}</span>
                  {deal.isEditable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingTitle(true)}
                      className="h-8 w-8 p-0"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                  )}
                </PageTitle>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCcw className="h-4 w-4" />
                Aktualisieren
              </Button>
              {user?.role === UserRole.User ? (
                <div className="px-4 py-2 border rounded-md bg-gray-50">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>Besitzer</span>
                  </div>
                  {derivedOwnerName && (
                    <div className="text-xs text-gray-500 mt-1 ml-6">
                      {derivedOwnerName}
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-2 border rounded-md bg-white">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <div className="leading-tight">
                        <div className="text-sm font-medium text-gray-900">{derivedOwnerName || '-'}</div>
                        <div className="text-xs text-gray-500">Besitzer</div>
                      </div>
                    </div>
                    <DropdownMenu open={ownerMenuOpen} onOpenChange={setOwnerMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="p-3 w-[320px]">
                        <div className="text-sm font-medium mb-2">An neuen Besitzer übergeben</div>
                        <Select
                          value={newOwnerId || deal.belongsToUserId || ''}
                          onValueChange={(val) => setNewOwnerId(val)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Neuen Besitzer auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {(deal.selectableEntities?.users || []).map((u) => (
                              <SelectItem key={u._id} value={u._id}>
                                {u.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex justify-end gap-2 mt-3">
                          <Button variant="outline" onClick={() => setOwnerMenuOpen(false)}>Abbrechen</Button>
                          <Button
                            onClick={async () => {
                              if (!newOwnerId || newOwnerId === deal.belongsToUserId) { setOwnerMenuOpen(false); return; }
                              await handleOwnerChange();
                              setOwnerMenuOpen(false);
                            }}
                          >
                            Speichern
                          </Button>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )}

              <Button
                onClick={() => setShowOutcomeModal(true)}
                disabled={!deal.isEditable}
                variant="outline"
              >
                <FileText className="h-4 w-4 mr-2" />
                Aktivität abschließen
              </Button>

              <Button
                onClick={() => toggleWin('win')}
                disabled={!deal.isEditable}
                variant="outline"
                className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              >
                <Check className="h-4 w-4 mr-2" />
                Gewonnen
              </Button>

              <Button
                onClick={() => toggleWin('lose')}
                disabled={!deal.isEditable}
                variant="outline"
                className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
              >
                <X className="h-4 w-4 mr-2" />
                Verloren
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={!deal.isEditable}
                    className="h-9 w-9"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => handleArchiveDeal()}
                    className="cursor-pointer"
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    Archivieren
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteDialog(true)}
                    className="cursor-pointer text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Deal Header with Stage, Pipeline, etc. */}
          <DealHeaderSimple deal={deal} ownerName={derivedOwnerName} />
        </div>
      </PageTitleContainer>

      {/* Main Content Area */}
      <div className="flex-1 px-6 py-4 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-4">
            {/* Left Column - Contact/Organization Card */}
            <div className="col-span-2">
            <Card className="h-fit">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">Kontakt</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {/* Person Info */}
                {deal.summaryPreview?.personSummary ? (
                  <div className="space-y-2">
                    {/* Contact Header - Compact */}
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-blue-100 text-blue-700 font-medium text-xs">
                          {deal.summaryPreview.personSummary.title
                            .split(' ')
                            .map(n => n[0])
                            .join('')
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm text-gray-900 truncate">
                          {deal.summaryPreview.personSummary.title}
                        </h3>
                      </div>
                    </div>

                    {/* Contact Details - Compact */}
                    {deal.personPreview && (
                      <div className="space-y-1">
                        {deal.personPreview.mail && (
                          <div className="flex items-start gap-2 text-xs">
                            <Mail className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
                            <a 
                              href={`mailto:${deal.personPreview.mail}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline break-all leading-relaxed"
                              title={deal.personPreview.mail}
                            >
                              {deal.personPreview.mail}
                            </a>
                          </div>
                        )}
                        {deal.personPreview.phone && (
                          <div className="flex items-start gap-2 text-xs">
                            <Phone className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
                            <a 
                              href={`tel:${deal.personPreview.phone}`}
                              className="text-gray-700 hover:text-gray-900 break-all leading-relaxed"
                              title={deal.personPreview.phone}
                            >
                              {deal.personPreview.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Users className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Kein Kontakt verknüpft</p>
                  </div>
                )}

                {/* Organization Info - Compact */}
                {deal.summaryPreview?.organizationSummary && (
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-xs text-gray-900 truncate">
                          {deal.summaryPreview.organizationSummary.title}
                        </h4>
                        {deal.organizationPreview?.address && (
                          <div className="flex items-start gap-1 mt-1">
                            <MapPin className="h-2.5 w-2.5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-gray-600 leading-relaxed break-words">
                              {deal.organizationPreview.address}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deal Details Card */}
            <Card className="mt-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Wert:</span>
                    <span className="font-medium text-gray-900">
                      {deal.salePreview?.totalGrossPrice ? `€${deal.salePreview.totalGrossPrice.toLocaleString('de-DE')}` : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Pipeline:</span>
                    <span className="text-gray-900 truncate max-w-20" title={deal.pipelineTitle || '-'}>
                      {deal.pipelineTitle || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Phase:</span>
                    <span className="text-gray-900 truncate max-w-20" title={deal.stageTitle || '-'}>
                      {deal.stageTitle || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Besitzer:</span>
                    <span className="text-gray-900 truncate max-w-20" title={derivedOwnerName || '-'}>
                      {derivedOwnerName || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Status:</span>
                    <div className="flex gap-1">
                      {deal.isWon && <Badge variant="default" className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800">Gewonnen</Badge>}
                      {deal.isArchived && <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Archiviert</Badge>}
                      {!deal.isWon && !deal.isArchived && <Badge variant="outline" className="text-xs px-1.5 py-0.5">Aktiv</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Erstellt:</span>
                    <span className="text-gray-900 text-xs">
                      {deal.createdAt
                        ? new Date(deal.createdAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '-'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

            {/* Middle Column - Tabs */}
            <div className="col-span-7">
            <Card className="h-full">
              <CardContent className="p-0 h-full">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                  <TabsList className="w-full justify-start rounded-none border-b h-12 px-2 overflow-x-auto flex-nowrap scrollbar-hide tabs-container" style={{ flexWrap: 'nowrap', minWidth: 'max-content' }}>
                    <TabsTrigger value="activity" className="gap-1 flex-shrink-0 text-xs px-2">
                      <Activity className="h-4 w-4" />
                      Aktivität
                    </TabsTrigger>
                    <TabsTrigger value="meetingplaner" className="gap-1 flex-shrink-0 text-xs px-2">
                      <Calendar className="h-4 w-4" />
                      Meetingplaner
                    </TabsTrigger>
                    <TabsTrigger value="call" className="gap-1 flex-shrink-0 text-xs px-2">
                      <Phone className="h-4 w-4" />
                      Anruf
                    </TabsTrigger>
                    <TabsTrigger value="emails" className="gap-1 flex-shrink-0 text-xs px-2">
                      <Mail className="h-4 w-4" />
                      E-Mail
                    </TabsTrigger>
                    <TabsTrigger value="files" className="gap-1 flex-shrink-0 text-xs px-2">
                      <Files className="h-4 w-4" />
                      Dateien
                      {filesCounts.files > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 px-1">
                          {filesCounts.files}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="gap-1 flex-shrink-0 text-xs px-2">
                      <FileText className="h-4 w-4" />
                      Dokumente
                      {filesCounts.documents > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 px-1">
                          {filesCounts.documents}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="invoice" className="gap-1 flex-shrink-0 text-xs px-2">
                      <FileText className="h-4 w-4" />
                      Rechnung
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-1 flex-shrink-0 text-xs px-2">
                      <Clock className="h-4 w-4" />
                      Verlauf
                    </TabsTrigger>
                  </TabsList>

                  <div className="flex-1 overflow-auto">
                    <TabsContent value="activity" className="h-full m-0 p-4">
                      <div className="space-y-4">
                        {/* Quick Activity Buttons */}
                        <div className="pb-4 border-b">
                          <QuickActivityButtons
                            dealId={id}
                            onActivityCreate={handleActivityCreate}
                            disabled={!deal.isEditable}
                          />
                        </div>

                        {/* Notes Section */}
                        <div id="notes-section" className="bg-gray-50 rounded-lg p-4">
                          <h3 className="font-medium mb-3 flex items-center">
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Notizen
                          </h3>
                          {deal.isEditable ? (
                            <InformationForm
                              defaultValue={deal.information || ''}
                              onSubmit={(info) => updateDeal('information', info.information)}
                              onCancel={() => {}}
                            />
                          ) : (
                            <p className="text-gray-600 whitespace-pre-wrap">
                              {deal.information || 'Keine Notizen vorhanden'}
                            </p>
                          )}
                        </div>

                        {/* Activity Timeline */}
                        <ActivityTimeline
                          dealId={id}
                          onActivityClick={handleActivityClick}
                          refreshTrigger={reload ? 1 : 0}
                        />
                      </div>
                    </TabsContent>


                    <TabsContent value="emails" className="h-full m-0 p-4 overflow-y-auto">
                      <EmailTemplates 
                        dealId={deal._id}
                        contactEmail={deal.personPreview?.mail}
                        contactName={deal.personPreview?.fullName}
                      />
                    </TabsContent>

                    <TabsContent value="files" className="h-full m-0 p-4">
                      <FileUpload 
                        dealId={deal._id} 
                        category="files" 
                        onFileChange={() => setReload(prev => !prev)}
                      />
                    </TabsContent>

                    <TabsContent value="history" className="h-full m-0 p-4 overflow-hidden">
                      <DealHistory key={`history-${reload ? 'a' : 'b'}`} item={deal} setReload={setReload} />
                    </TabsContent>

                    <TabsContent value="meetingplaner" className="h-full m-0 p-4">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium">Termine für diesen Deal</h3>
                          <Button onClick={() => setShowAppointmentModal(true)} disabled={!deal.isEditable}>
                            Neuer Termin
                          </Button>
                        </div>
                        <DealCalendar dealId={id} key={`calendar-${reload}`} />
                      </div>
                    </TabsContent>

                    <TabsContent value="call" className="h-full m-0 p-4">
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">Anrufen</h3>
                        <div className="border rounded-lg p-6 text-center space-y-4">
                          <Phone className="h-12 w-12 mx-auto text-gray-400" />
                          <p className="text-gray-600">Anrufmethode: Computer</p>
                          <div className="space-y-2">
                            <p className="font-medium">{deal.personPreview?.phone || '+49 26139027446'}</p>
                            <Button className="w-full max-w-xs" variant="default">
                              Anruf beginnen
                            </Button>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="documents" className="h-full m-0 p-4">
                      <FileUpload 
                        dealId={deal._id} 
                        category="documents" 
                        onFileChange={() => setReload(prev => !prev)}
                      />
                    </TabsContent>

                    <TabsContent value="invoice" className="h-full m-0 p-4">
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">Rechnungen</h3>
                        <p className="text-gray-600">Noch keine Rechnungen vorhanden.</p>
                      </div>
                    </TabsContent>
                  </div>
                </Tabs>
              </CardContent>
            </Card>
            </div>
            
            {/* Right Column - Calendar */}
            <div className="col-span-3">
              <DealCalendarSidebar key={`sidebar-${reload ? 'a' : 'b'}`} dealId={id} />
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Modal */}
      <AppointmentModalWrapper
        isOpen={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        appointment={selectedAppointment}
        dealId={id}
        dealName={deal.title}
        contactId={deal.belongsToPersonId}
        contactName={deal.summaryPreview?.personSummary?.title}
        onSave={handleAppointmentSave}
      />

      {/* Outcome Modal */}
      {deal && (
        <DealOutcomeModal
          isOpen={showOutcomeModal}
          onClose={() => setShowOutcomeModal(false)}
          onSave={handleOutcomeSave}
          dealId={deal._id}
          dealTitle={deal.title}
          requireNextStep={true}
        />
      )}

      {/* Owner Change Dialog removed: inline Select is used instead */}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Deal löschen</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Sind Sie sicher, dass Sie diesen Deal unwiderruflich löschen möchten?
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <strong>"{deal?.title}"</strong> wird dauerhaft entfernt und kann nicht wiederhergestellt werden.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleDeleteDeal}
              variant="destructive"
            >
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Appointment Modal Wrapper
const AppointmentModalWrapper = ({
  isOpen,
  onClose,
  appointment,
  dealId,
  dealName,
  contactId,
  contactName,
  onSave
}: {
  isOpen: boolean;
  onClose: () => void;
  appointment?: Appointment | null;
  dealId: string;
  dealName: string;
  contactId?: string;
  contactName?: string;
  onSave: (data: CreateAppointmentDto | UpdateAppointmentDto, id?: string) => Promise<void>;
}) => {
  const { deleteAppointment, markAsComplete } = useAppointments();

  const handleSave = async (data: CreateAppointmentDto | UpdateAppointmentDto, id?: string) => {
    await onSave(data, id);
  };

  const handleDelete = async (id: string) => {
    await deleteAppointment(id);
    onClose();
  };

  const handleMarkComplete = async (id: string, notes?: string) => {
    await markAsComplete(id, notes);
    onClose();
  };

  return (
    <AppointmentModal
      isOpen={isOpen}
      onClose={onClose}
      appointment={appointment}
      defaultDealId={dealId}
      defaultDealName={dealName}
      defaultContactId={contactId}
      defaultContactName={contactName}
      onSave={handleSave}
      onDelete={appointment ? handleDelete : undefined}
      onMarkComplete={appointment ? handleMarkComplete : undefined}
    />
  );
};

export default DealPageEnhanced;
